import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AgentAssistantProposal,
  AgentWorkspace,
} from '@servicesai/api-client';
import {
  GlassButton,
  GlassCard,
  GlassTextarea,
  StatusPill,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';
import { displayModel } from '../../lib/aiBranding.js';

export function AgentConfigurationAssistant({
  slug,
  workspace,
  selectedNoteIds,
}: {
  slug: string;
  workspace: AgentWorkspace;
  selectedNoteIds: string[];
}) {
  const { l, lang } = useI18n();
  const queryClient = useQueryClient();
  const draft = workspace.versions.find((version) => version.state === 'draft') ?? null;
  const [message, setMessage] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<AgentAssistantProposal | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setSessionId(null);
    setProposal(null);
  }, [draft?.id]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
      queryClient.invalidateQueries({ queryKey: ['agent-detail', slug] }),
      queryClient.invalidateQueries({ queryKey: ['agent-portfolio'] }),
    ]);
  };

  const createDraft = useMutation({
    mutationFn: () => admin().createAgentDraft(slug, workspace.publishedVersion?.id),
    onSuccess: refresh,
  });

  const propose = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error(l(S.assistantDraftRequired));
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const created = await admin().createAgentAssistantSession(slug, draft.id);
        currentSessionId = created.session.id;
        setSessionId(currentSessionId);
      }
      return admin().proposeAgentConfiguration(slug, currentSessionId, {
        message: message.trim(),
        noteIds: selectedNoteIds,
        lang,
      });
    },
    onSuccess: ({ proposal: next }) => {
      setProposal(next);
      setMessage('');
    },
  });

  const apply = useMutation({
    mutationFn: async () => {
      if (!draft || !sessionId || !proposal) throw new Error(l(S.assistantProposalMissing));
      return admin().applyAgentConfigurationProposal(
        slug,
        sessionId,
        proposal.id,
        draft.lock_version,
      );
    },
    onSuccess: async () => {
      setProposal(null);
      await refresh();
    },
  });

  const reject = useMutation({
    mutationFn: async () => {
      if (!sessionId || !proposal) throw new Error(l(S.assistantProposalMissing));
      return admin().rejectAgentConfigurationProposal(slug, sessionId, proposal.id);
    },
    onSuccess: () => setProposal(null),
  });

  const error = createDraft.error ?? propose.error ?? apply.error ?? reject.error;
  const busy = createDraft.isPending || propose.isPending || apply.isPending || reject.isPending;

  return (
    <aside className="2xl:sticky 2xl:top-4 2xl:self-start" aria-label={l(S.configurationAssistant)}>
      <GlassCard className="overflow-hidden" strong>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between gap-3 border-b border-aeblack-100 p-4 text-start"
        >
          <span>
            <span className="block text-base font-extrabold">{l(S.configurationAssistant)}</span>
            <span className="mt-0.5 block text-[11px] text-ink-faint">{l(S.configurationAssistantHint)}</span>
          </span>
          <span className="flex items-center gap-2">
            <StatusPill
              state={draft ? 'in_progress' : 'draft'}
              label={draft ? `${l(S.draft)} v${draft.version}` : l(S.noDraft)}
            />
            <span aria-hidden>{expanded ? '−' : '+'}</span>
          </span>
        </button>

        {expanded && (
          <div className="space-y-4 p-4">
            {!draft ? (
              <div className="rounded-lg border border-aegold-200 bg-aegold-50 p-3">
                <p className="text-xs leading-relaxed text-ink-soft">{l(S.assistantDraftRequired)}</p>
                <GlassButton
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  disabled={busy}
                  onClick={() => createDraft.mutate()}
                >
                  {l(S.createDraft)}
                </GlassButton>
              </div>
            ) : (
              <>
                <div className="rounded-lg bg-aeblack-50 p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold">{l(S.savedNotesContext)}</span>
                    <StatusPill
                      state={selectedNoteIds.length ? 'in_progress' : 'draft'}
                      label={`${selectedNoteIds.length} ${l(S.selected)}`}
                    />
                  </div>
                  <p className="mt-1 text-ink-faint">{l(S.assistantNotesHint)}</p>
                </div>
                <label className="block text-xs font-bold text-ink-soft">
                  {l(S.configurationRequest)}
                  <GlassTextarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    rows={6}
                    maxLength={20_000}
                    className="mt-1"
                    placeholder={l(S.configurationRequestPlaceholder)}
                  />
                  <span className="mt-1 block text-end font-normal text-ink-faint">
                    {message.length.toLocaleString()} / 20,000
                  </span>
                </label>
                <GlassButton
                  variant="primary"
                  className="w-full justify-center"
                  disabled={busy || !message.trim()}
                  onClick={() => propose.mutate()}
                >
                  {propose.isPending ? l(S.generatingProposal) : l(S.generateProposal)}
                </GlassButton>
              </>
            )}

            {error && (
              <div className="aegov-alert alert-error" role="alert">
                <div className="alert-content text-xs">{(error as Error).message}</div>
              </div>
            )}

            {proposal && (
              <div className="space-y-3 border-t border-aeblack-100 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-extrabold">{proposal.summary}</div>
                    <div className="mt-0.5 text-[9px] text-ink-faint">{l(displayModel(proposal.model))}</div>
                  </div>
                  <StatusPill
                    state={proposal.validation.valid ? 'published' : 'rejected'}
                    label={proposal.validation.valid ? l(S.validated) : l(S.invalid)}
                  />
                </div>
                <p className="text-xs leading-relaxed text-ink-soft">{proposal.rationale}</p>

                {proposal.notes.length > 0 && (
                  <div className="rounded-lg border border-aeblack-100 p-3">
                    <div className="text-[11px] font-bold">{l(S.noteProvenance)}</div>
                    <ul className="mt-2 space-y-1 text-[10px] text-ink-faint">
                      {proposal.notes.map((note) => (
                        <li key={`${note.id}-${note.revision}`}>
                          <code>{note.id.slice(0, 8)}</code> · r{note.revision} — {note.influence}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-lg border border-aeblack-100">
                  <div className="flex items-center justify-between bg-aeblack-50 px-3 py-2 text-[11px]">
                    <strong>{l(S.configurationDiff)}</strong>
                    <span>{proposal.changes.length} {l(S.changes)}</span>
                  </div>
                  <div className="max-h-64 overflow-auto">
                    {proposal.changes.length === 0 ? (
                      <p className="p-3 text-xs text-ink-faint">{l(S.noConfigurationChanges)}</p>
                    ) : (
                      proposal.changes.map((change) => (
                        <details key={change.section} className="border-t border-aeblack-100 p-3">
                          <summary className="cursor-pointer text-xs font-bold">{change.section}</summary>
                          <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded bg-aeblack-50 p-2 text-[9px]" dir="ltr">
                            {JSON.stringify(change.after, null, 2)}
                          </pre>
                        </details>
                      ))
                    )}
                  </div>
                </div>

                {proposal.impacts.length > 0 && (
                  <ul className="space-y-1 text-xs text-ink-soft">
                    {proposal.impacts.map((impact, index) => <li key={index}>• {impact}</li>)}
                  </ul>
                )}
                {proposal.validation.issues.length > 0 && (
                  <div className="aegov-alert alert-error">
                    <div className="alert-content text-xs">
                      {proposal.validation.issues.map((issue) => (
                        <div key={`${issue.path}-${issue.message}`}><code>{issue.path}</code>: {issue.message}</div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <GlassButton
                    variant="primary"
                    className="flex-1 justify-center"
                    disabled={busy || !proposal.validation.valid || proposal.patch.length === 0}
                    onClick={() => apply.mutate()}
                  >
                    {apply.isPending ? l(S.applyingProposal) : l(S.applyToDraft)}
                  </GlassButton>
                  <GlassButton
                    variant="danger"
                    disabled={busy}
                    onClick={() => reject.mutate()}
                  >
                    {l(S.reject)}
                  </GlassButton>
                </div>
                <p className="text-[10px] leading-relaxed text-ink-faint">{l(S.assistantApprovalReminder)}</p>
              </div>
            )}
          </div>
        )}
      </GlassCard>
    </aside>
  );
}
